import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Download, ExternalLink, ZoomIn, ZoomOut, RotateCw, RefreshCcw, Loader2, PenLine } from "lucide-react";
import MarkupEditor from "@/components/files/MarkupEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");
const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"];

export const isImage = (f) => (f?.content_type || "").toLowerCase().startsWith("image/") || IMG_EXT.includes(extOf(f?.original_filename));
export const isPdf = (f) => (f?.content_type || "").toLowerCase() === "application/pdf" || extOf(f?.original_filename) === "pdf";
export const canPreview = (f) => isImage(f) || isPdf(f);
export const fileUrl = (f, inline = false) => `${BACKEND}/api/files/${f.storage_path}${inline ? "?inline=1" : ""}`;

let _pdfjsPromise = null;
function loadPdfjs() {
  if (!_pdfjsPromise) {
    _pdfjsPromise = import("pdfjs-dist/build/pdf").then((mod) => {
      const lib = mod.getDocument ? mod : mod.default;
      lib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${lib.version}/pdf.worker.min.js`;
      return lib;
    });
  }
  return _pdfjsPromise;
}

const MAX_PDF_PAGES = 50;

const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return ""; }
};
export const downloadFile = (f) => {
  if (!f) return;
  const a = document.createElement("a");
  a.href = fileUrl(f);
  a.download = f.original_filename || "download";
  document.body.appendChild(a); a.click(); a.remove();
};

export default function FilePreviewSheet({ file, onClose, onSaveMarkup }) {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pages, setPages] = useState([]); // [{ url, width, height }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [markup, setMarkup] = useState(false);
  const objectUrlsRef = useRef([]);

  const img = file && isImage(file);
  const pdf = file && isPdf(file);

  useEffect(() => { setScale(1); setRotation(0); setMarkup(false); }, [file?.file_id, file?.storage_path]);

  // Fetch + render via the authenticated API client (blob) so previews never depend on the
  // browser's native PDF viewer (which downloads instead of rendering).
  useEffect(() => {
    let alive = true;
    const revokeAll = () => { objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)); objectUrlsRef.current = []; };
    setPages([]); setError(false);
    if (!file || !canPreview(file)) { revokeAll(); return undefined; }

    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/files/${file.storage_path}`, { responseType: "blob" });
        if (!alive) return;
        if (isImage(file)) {
          const url = URL.createObjectURL(res.data);
          objectUrlsRef.current.push(url);
          const dim = await new Promise((resolve) => {
            const im = new Image();
            im.onload = () => resolve({ width: im.naturalWidth || 1024, height: im.naturalHeight || 768 });
            im.onerror = () => resolve({ width: 1024, height: 768 });
            im.src = url;
          });
          if (alive) setPages([{ url, ...dim }]);
        } else if (isPdf(file)) {
          const buf = await res.data.arrayBuffer();
          const pdfjsLib = await loadPdfjs();
          const doc = await pdfjsLib.getDocument({ data: buf }).promise;
          const count = Math.min(doc.numPages, MAX_PDF_PAGES);
          const acc = [];
          for (let i = 1; i <= count; i += 1) {
            if (!alive) break;
            const page = await doc.getPage(i);
            const base = page.getViewport({ scale: 1 });
            const target = Math.max(1.3, 1400 / base.width);
            const vp = page.getViewport({ scale: target });
            const canvas = document.createElement("canvas");
            canvas.width = Math.ceil(vp.width);
            canvas.height = Math.ceil(vp.height);
            await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
            const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
            if (!blob || !alive) break;
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.push(url);
            acc.push({ url, width: canvas.width, height: canvas.height });
            if (alive) setPages([...acc]);
          }
        }
      } catch (e) {
        if (alive) setError(true);
        if (process.env.NODE_ENV === "development") console.warn("Preview render failed:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; revokeAll(); };
  }, [file?.file_id, file?.storage_path]);

  const transform = `scale(${scale}) rotate(${rotation}deg)`;
  const previewable = file && canPreview(file);
  const rendered = pages.length > 0 && !error;
  const canMarkup = !!onSaveMarkup && previewable && rendered;

  const saveMarkup = async (markedFile) => {
    await onSaveMarkup(markedFile);
    setMarkup(false);
    onClose();
  };

  return (
    <Sheet open={!!file} onOpenChange={(o) => { if (!o) { setMarkup(false); onClose(); } }}>
      <SheetContent side="right" className={`w-full flex flex-col ${markup ? "sm:max-w-4xl" : "sm:max-w-2xl"}`} data-testid="file-preview-panel">
        <SheetHeader>
          <SheetTitle className="truncate pr-8">{markup ? "Markup · " : ""}{file?.original_filename}</SheetTitle>
        </SheetHeader>

        {markup && pages.length > 0 ? (
          <div className="flex-1 min-h-0 mt-2">
            <MarkupEditor
              pages={pages}
              isPdf={!!pdf}
              filename={file?.original_filename || "file"}
              onCancel={() => setMarkup(false)}
              onSave={saveMarkup}
            />
          </div>
        ) : (
          <>
            {previewable && (
              <div className="flex items-center gap-1 mt-3 flex-wrap" data-testid="file-preview-zoom-controls">
                <button onClick={() => setScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))} data-testid="file-preview-zoom-out"
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors" title="Zoom out">
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="text-xs text-zinc-500 w-12 text-center tabular-nums">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale((s) => Math.min(5, +(s + 0.25).toFixed(2)))} data-testid="file-preview-zoom-in"
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors" title="Zoom in">
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="file-preview-rotate"
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors ml-1" title="Rotate">
                  <RotateCw className="h-4 w-4" />
                </button>
                <button onClick={() => { setScale(1); setRotation(0); }} data-testid="file-preview-reset"
                  className="h-8 w-8 flex items-center justify-center rounded-sm border border-zinc-200 hover:bg-zinc-100 transition-colors" title="Reset">
                  <RefreshCcw className="h-4 w-4" />
                </button>
                {canMarkup && (
                  <button onClick={() => setMarkup(true)} data-testid="file-preview-markup"
                    className="inline-flex items-center gap-1.5 h-8 px-3 ml-auto rounded-sm bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-800 transition-colors" title="Add markups">
                    <PenLine className="h-3.5 w-3.5" /> Markup
                  </button>
                )}
              </div>
            )}

            <div className="flex-1 min-h-0 mt-3 overflow-auto bg-zinc-100 rounded-md" data-testid="file-preview-body">
              {loading && pages.length === 0 && (
                <div className="h-full flex items-center justify-center text-zinc-400 gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" /> <span className="text-sm">Loading preview…</span>
                </div>
              )}

              {error && (
                <div className="h-full flex flex-col items-center justify-center gap-3 p-8 text-center">
                  <p className="text-sm text-zinc-500 max-w-sm">Couldn’t render a preview — this file may be incomplete or corrupt. Try downloading it, or re-upload the original.</p>
                  <button onClick={() => downloadFile(file)} className="inline-flex items-center gap-2 bg-zinc-900 text-white h-9 px-4 rounded-sm text-sm font-semibold hover:bg-zinc-800 transition-colors">
                    <Download className="h-4 w-4" /> Download instead
                  </button>
                </div>
              )}

              {img && pages.length > 0 && (
                <div className="flex items-center justify-center p-4 min-h-full">
                  <img src={pages[0].url} alt={file.original_filename} data-testid="file-preview-image"
                    className="max-w-full object-contain"
                    style={{ transform, transformOrigin: "center", transition: "transform 0.15s ease" }} />
                </div>
              )}

              {pdf && pages.length > 0 && (
                <div className="flex flex-col items-center gap-4 p-4" data-testid="file-preview-pdf">
                  {pages.map((pg, i) => (
                    <img key={pg.url} src={pg.url} alt={`${file.original_filename} page ${i + 1}`}
                      className="max-w-full shadow-sm bg-white"
                      style={{ transform, transformOrigin: "top center", transition: "transform 0.15s ease" }} />
                  ))}
                  {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
                </div>
              )}

              {file && !canPreview(file) && !loading && (
                <div className="h-full flex items-center justify-center">
                  <p className="text-sm text-zinc-500 p-8">No preview available for this file type.</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-4">
              <button onClick={() => downloadFile(file)} data-testid="file-preview-download"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white h-10 px-4 rounded-sm text-sm font-semibold hover:bg-zinc-800 transition-colors">
                <Download className="h-4 w-4" /> Download
              </button>
              {previewable && (
                <button onClick={() => window.open(fileUrl(file, true), "_blank")} data-testid="file-preview-newtab"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm font-semibold hover:bg-zinc-100 transition-colors">
                  <ExternalLink className="h-4 w-4" /> Open in new tab
                </button>
              )}
              <span className="text-xs text-zinc-400 ml-auto">{[fmtSize(file?.size), fmtDate(file?.uploaded_at)].filter(Boolean).join(" · ")}</span>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
